import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { Influencer } from "@/types/influencer";
import { Instagram, Youtube, Facebook, Twitter } from "lucide-react";
import { FaTiktok } from "react-icons/fa";

interface SocialProfilesProps {
  influencer: Influencer;
}

// Helper to build proper social URL
const buildSocialUrl = (platform: string, handle: string): string => {
  // If it's already a full URL, return as-is
  if (handle.startsWith('http://') || handle.startsWith('https://')) {
    return handle;
  }
  
  // Otherwise, build the URL based on platform
  const cleanHandle = handle.replace('@', '');
  switch (platform) {
    case 'instagram':
      return `https://instagram.com/${cleanHandle}`;
    case 'youtube':
      return `https://youtube.com/@${cleanHandle}`;
    case 'tiktok':
      return `https://tiktok.com/@${cleanHandle}`;
    case 'facebook':
      return `https://facebook.com/${cleanHandle}`;
    case 'twitter':
      return `https://twitter.com/${cleanHandle}`;
    default:
      return handle;
  }
};

// Helper to extract display handle from URL or handle
const getDisplayHandle = (handle: string): string => {
  // If it's a URL, extract the username part
  if (handle.startsWith('http://') || handle.startsWith('https://')) {
    const parts = handle.split('/');
    const lastPart = parts[parts.length - 1] || parts[parts.length - 2];
    return `@${lastPart.replace('@', '')}`;
  }
  // Otherwise, format as @handle
  return handle.startsWith('@') ? handle : `@${handle}`;
};

export const SocialProfiles = ({ influencer }: SocialProfilesProps) => {
  const { t } = useLanguage();
  
  if (!influencer.socialLinks) return null;
  
  const { instagram, instagram_followers, youtube, youtube_followers, tiktok, tiktok_followers, facebook, facebook_followers, twitter, twitter_followers } = influencer.socialLinks;
  
  // Check if any social links exist
  const hasSocialLinks = instagram || youtube || tiktok || facebook || twitter;
  if (!hasSocialLinks) return null;
  
  return (
    <Card className="border-primary/5 overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
      <CardContent className="pt-6 relative z-10">
        <h3 className="text-xl font-semibold mb-6 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
          {t("socialProfiles")}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {instagram && (
            <a 
              href={buildSocialUrl('instagram', instagram)} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 hover:shadow-md transition-all hover:bg-gradient-to-r hover:from-primary/20 hover:to-primary/10"
            >
              <Instagram className="h-5 w-5 mr-3 text-primary flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="truncate">{getDisplayHandle(instagram)}</span>
                {instagram_followers && (
                  <span className="text-xs text-muted-foreground">{instagram_followers} followers</span>
                )}
              </div>
            </a>
          )}
          
          {youtube && (
            <a 
              href={buildSocialUrl('youtube', youtube)} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 hover:shadow-md transition-all hover:bg-gradient-to-r hover:from-primary/20 hover:to-primary/10"
            >
              <Youtube className="h-5 w-5 mr-3 text-primary flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="truncate">{getDisplayHandle(youtube)}</span>
                {youtube_followers && (
                  <span className="text-xs text-muted-foreground">{youtube_followers} subscribers</span>
                )}
              </div>
            </a>
          )}
          
          {tiktok && (
            <a 
              href={buildSocialUrl('tiktok', tiktok)} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 hover:shadow-md transition-all hover:bg-gradient-to-r hover:from-primary/20 hover:to-primary/10"
            >
              <FaTiktok className="h-5 w-5 mr-3 text-primary flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="truncate">{getDisplayHandle(tiktok)}</span>
                {tiktok_followers && (
                  <span className="text-xs text-muted-foreground">{tiktok_followers} followers</span>
                )}
              </div>
            </a>
          )}
          
          {facebook && (
            <a 
              href={buildSocialUrl('facebook', facebook)} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 hover:shadow-md transition-all hover:bg-gradient-to-r hover:from-primary/20 hover:to-primary/10"
            >
              <Facebook className="h-5 w-5 mr-3 text-primary flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="truncate">{getDisplayHandle(facebook)}</span>
                {facebook_followers && (
                  <span className="text-xs text-muted-foreground">{facebook_followers} followers</span>
                )}
              </div>
            </a>
          )}
          
          {twitter && (
            <a 
              href={buildSocialUrl('twitter', twitter)} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 hover:shadow-md transition-all hover:bg-gradient-to-r hover:from-primary/20 hover:to-primary/10"
            >
              <Twitter className="h-5 w-5 mr-3 text-primary flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="truncate">{getDisplayHandle(twitter)}</span>
                {twitter_followers && (
                  <span className="text-xs text-muted-foreground">{twitter_followers} followers</span>
                )}
              </div>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
